import React, {
  ReactNode,
  ReactElement,
  useContext,
  useMemo,
  useEffect,
  useState,
} from 'react'
import { useDispatch } from 'react-redux'
import { setDeliveryDate } from '@mindfulchefuk/actions/basketActions'
import { useQueryHomepageDateAvailabilities } from '@mindfulchefuk/features/Homepage/services/useQueryHomepageDateAvailabilities'
import { CMSHomepageVariant } from '@mindfulchefuk/features/CMS/interfaces'
import { useServerSideQueryHomepageCMS } from '@mindfulchefuk/features/Homepage/services/useServerSideQueryHomepageCMS'
import { homepageVariantFallback } from '@mindfulchefuk/features/Homepage/data/homepage-variant'
import { useGetRecipeAvailabilities } from '@mindfulchefuk/hooks/useGetRecipeAvailabilities'
import { TRecipeAvailabilityCarouselCard } from '@mindfulchefuk/types/recipeAvailabilities'
import { dateFormats } from '@mindfulchefuk/lib/dateFormats'

type THomepageContext = {
  isLoading: boolean
  CMSHomepageData: CMSHomepageVariant
  recipesToDisplayInRecipeCarousel: TRecipeAvailabilityCarouselCard[]
}

const HomepageContext = React.createContext<THomepageContext | undefined>(
  undefined
)

export const useHomepageContext = () => {
  const context = useContext(HomepageContext)

  if (context === undefined) {
    throw new Error(
      'useHomepageContext must be used within a HomepageContextProvider'
    )
  }

  return context
}

export const HomepageContextProvider = ({
  children,
}: {
  children: ReactNode
}): ReactElement => {
  const dispatch = useDispatch()
  const [CMSHomepageContent, setCMSHomepageContent] =
    useState<null | CMSHomepageVariant>(null)
  const { data: dateDeliveritities, isLoading: isLoadingDateAvailabilities } =
    useQueryHomepageDateAvailabilities()

  const {
    data: cmsHomepageData,
    isLoading: isLoadingCMSHomepageData,
    isFetched: hasFetchedCMSHomepageData,
  } = useServerSideQueryHomepageCMS()

  const {
    data: recipeAvailabilities,
    isLoading: isLoadingRecipeAvailabilities,
  } = useGetRecipeAvailabilities({
    deliveryDate: dateFormats.iso(new Date()),
    portionCount: 2,
    mealPlan: 'Balanced',
  })

  const recipesToDisplayInRecipeCarousel: TRecipeAvailabilityCarouselCard[] =
    useMemo(
      () =>
        recipeAvailabilities?.map(({ recipe }) => ({
          title: recipe.title,
          image: recipe.image,
          calories: recipe.macrosPerPerson['2Portion'].calories,
          isVegan: recipe.foodGroup === 'Vegan',
          cookingTime: recipe.cookingTime,
        })),
      [recipeAvailabilities]
    )

  useEffect(() => {
    if (dateDeliveritities) {
      const firstAvailableDateForDelivery = dateDeliveritities.find(
        (deliverability) => deliverability.recipeDeliverable
      )

      dispatch(setDeliveryDate(firstAvailableDateForDelivery.deliveryDate))
    }
  }, [dateDeliveritities, dispatch])

  // This is temporary until I remove the CMS variants in a follow up PR
  useEffect(() => {
    if (hasFetchedCMSHomepageData) {
      const homepageData = cmsHomepageData.variants.find(
        (variant) => variant.path === '/'
      )

      setCMSHomepageContent({
        ...homepageData,
        howItWorks: homepageVariantFallback.howItWorks,
      })
    }
  }, [hasFetchedCMSHomepageData, cmsHomepageData])

  const isLoading =
    isLoadingDateAvailabilities ||
    isLoadingCMSHomepageData ||
    isLoadingRecipeAvailabilities

  const contextValue = useMemo(
    () => ({
      isLoading,
      CMSHomepageData: CMSHomepageContent,
      recipesToDisplayInRecipeCarousel,
    }),
    [isLoading, CMSHomepageContent, recipesToDisplayInRecipeCarousel]
  )

  return (
    <HomepageContext.Provider value={contextValue}>
      {children}
    </HomepageContext.Provider>
  )
}
